import type {
	GeoJSONLineString,
	GeoJSONPolygon,
	SensorStatus,
	ZoneBoundary,
} from "@sitescene/types";

import {
	buildZoneBoundsFeature,
	DEFAULT_ZONE_BOUNDS_SHAPE,
	expandGeoJsonPolygonToIncludePoints,
	storedBoundaryToLineString,
	toZoneBoundsOverlay,
	type ZoneBoundsShape,
} from "@sitescene/shared";
import { normalizeZoneColor } from "@sitescene/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { floorsApi } from "@/api/floors";
import { sensorsApi } from "@/api/sensors";
import { zonesApi } from "@/api/zones";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";

import type {
	ZoneBoundaryDrawAction,
	ZoneBoundaryDrawConfig,
} from "../../map/draw/useMapboxDraw";
import type {
	ShadowLocation,
	ShadowSensor,
	ShadowZone,
} from "../../types/location-map";
import type { MapDrillBreadcrumb } from "../../utils/location/location-map-utils";

import { buildFloorBoundaryOverlays } from "../../map/floors/floor-boundary-overlays";
import {
	createSensorMarkerFeature,
	isClusterMarkerFeature,
	isSensorMarkerFeature,
} from "../../map/markers/create-marker-feature";
import { getRefId } from "../../utils/location/installation-ids";
import {
	buildAutoZoneDrawPolygon,
	buildLocationClusterMarkers,
	buildLocationMapMarkers,
	buildLocationMapOverlays,
	buildLocationZoneClusterMarkers,
	getLocationMapCenter,
	getLocationMapDataFingerprint,
	getLocationsMapDataFingerprint,
	partitionLocations,
} from "../../utils/location/location-map-features";
import {
	applySensorCoordinateOverridesToMarkers,
	applySensorCoordinatesToSensors,
	areSensorEditDraftsEqual,
	areSensorLngLatByIdEqual,
	buildLocationMapBreadcrumbs,
	buildSensorUpdateFromEditDraft,
	buildZoneUpdateFromEditDraft,
	canPersistSensorEditDraft,
	canPersistZoneEditDraft,
	createSensorEditDraft,
	createZoneEditDraft,
	EMPTY_MAP_LOCATION,
	EMPTY_ZONES,
	filterMapFeaturesByZones,
	findSensorInLocation,
	focusMapFeaturesByZones,
	getCoordinateStringsFromSensor,
	getFloorSortOrderUpdates,
	getMarkerZoneId,
	getOverlayZoneId,
	getPolygonTranslationDelta,
	getSensorEditDraftFingerprint,
	getSensorsOutsideZoneBoundary,
	getZoneEditDraftFingerprint,
	isZoneEditDraftBoundarySelfIntersecting,
	normalizeZoneBorderWidth,
	parseSensorEditDraftCoordinates,
	pickRicherLineString,
	polygonToLineStringBoundary,
	resolveMapFloorScope,
	resolveZoneBoundaryKind,
	resolveZoneBoundaryMode,
	seedManualZoneBoundary,
	SENSOR_TYPE_OPTIONS,
	type SensorLngLatById,
	type ShadowSensorEditDraft,
	type ShadowZoneEditDraft,
	snapSensorLngLatByIdToLine,
	snapshotMappedSensorCoordinates,
	sortFloorsForList,
	sortZonesForMapList,
	translateSensorLngLatById,
} from "../../utils/location/location-map-utils";
import {
	toShadowFloorUpdate,
	toShadowSensorUpdate,
	toShadowZoneUpdate,
} from "../../utils/location/to-shadow-wire";
import {
	isSensorEditEnterTarget,
	isSensorEditHotkeyBlocked,
	SENSOR_AUTO_SAVE_DELAY_MS,
	type SensorAutoSaveStatus,
} from "../../utils/sensor/sensor-auto-save";
import { coordinatesFromMapFitFeature } from "../../utils/shared/coordinates";
import {
	resolveLocationMapFocus,
	toLocationMapFocusEntities,
} from "../../utils/shared/location-map-focus-search-params";
import { toTitleCase } from "../../utils/shared/strings";
import { buildZoneBoundaryDraw } from "./build-zone-boundary-draw";
import { resolveEditAutoSaveStatus } from "./edit-auto-save-status";
import {
	applySensorCoordinatesToLocation,
	cloneLocation,
	mergeZonePatch,
	updateSensorInLocation,
	updateZoneInLocation,
} from "./location-tree-updates";

const EMPTY_LOCATION_IDS_WITH_INSTALLATION: ReadonlySet<string> = new Set();

type PersistSensorDraftOptions = {
	closeOnSuccess?: boolean;
	silent?: boolean;
	/** Restore an earlier checkpoint without recording a new undo step. */
	skipUndoCheckpoint?: boolean;
};

type PersistZoneDraftOptions = {
	closeOnSuccess?: boolean;
	silent?: boolean;
	skipUndoCheckpoint?: boolean;
};

type ZoneEditHistoryEntry = {
	draft: ShadowZoneEditDraft;
	sensorCoordinates: SensorLngLatById;
};

/**
 * Map and list state for Location Management: which zone is focused, which
 * sensor is being edited, and whether unfocused zones should fade or hide.
 */
export const useLocationManagementState = (options?: {
	dimUnfocusedZones?: boolean;
	/**
	 * When set, keep this location selected (single-location route). List/map
	 * stay drilled into that location instead of the all-locations overview.
	 */
	focusedLocationId?: string;
	/**
	 * Location ids that already have at least one installation.
	 * Used for location-cluster “missing setup” tooltips on the overview.
	 */
	locationIdsWithInstallation?: ReadonlySet<string>;
	onInteractionChange?: (isInteracting: boolean) => void;
	/**
	 * When set, overview location-cluster clicks call this instead of
	 * {@link selectLocation} (e.g. navigate to `/locations/:id`).
	 */
	onLocationClusterSelect?: (locationId: string) => void;
	/**
	 * Fired when the selected location changes via {@link selectLocation}
	 * (map cluster click, list, select, etc.).
	 */
	onLocationSelected?: (locationId: string) => void;
	/** Called after a successful Shadow mutate so React Query can refetch. */
	onLocationsMutated?: () => void;
	/**
	 * Fired when floor/zone/sensor map focus changes so the host can sync URL
	 * query params (`floor`, `zone`, `sensor`). Pass `null` to clear a param.
	 */
	onMapFocusChange?: (focus: {
		floorId?: null | string;
		sensorId?: null | string;
		zoneId?: null | string;
	}) => void;
	/** API-adapted locations to sync from (org load / refetch). */
	sourceLocations?: null | ShadowLocation[];
	/** When this revision changes, replace local locations from `sourceLocations`. */
	sourceRevision?: null | number | string;
	/**
	 * Floor/zone/sensor ids currently in the URL. Used before local selection
	 * state is hydrated so the map does not flash the default floor.
	 */
	urlMapFocus?: {
		floorId?: null | string;
		sensorId?: null | string;
		zoneId?: null | string;
	};
	zoneBoundsShape?: ZoneBoundsShape;
}) => {
	const dimUnfocusedZones = options?.dimUnfocusedZones ?? true;
	const zoneBoundsShape = options?.zoneBoundsShape ?? DEFAULT_ZONE_BOUNDS_SHAPE;
	const focusedLocationId = options?.focusedLocationId;
	const urlMapFocus = options?.urlMapFocus;
	const locationIdsWithInstallation =
		options?.locationIdsWithInstallation ??
		EMPTY_LOCATION_IDS_WITH_INSTALLATION;
	const onInteractionChange = options?.onInteractionChange;
	const onLocationsMutated = options?.onLocationsMutated;
	const onLocationSelected = options?.onLocationSelected;
	const onLocationClusterSelect = options?.onLocationClusterSelect;
	const onMapFocusChange = options?.onMapFocusChange;
	const sourceLocations = options?.sourceLocations;
	const sourceRevision = options?.sourceRevision;
	const canCustomizeLocationMap = true;
	const canEditSensorDetails = true;
	const canEditZoneDetails = true;

	const onLocationsMutatedRef = useRef(onLocationsMutated);
	const onLocationSelectedRef = useRef(onLocationSelected);
	const onLocationClusterSelectRef = useRef(onLocationClusterSelect);
	const onMapFocusChangeRef = useRef(onMapFocusChange);

	const [locations, setLocations] = useState<ShadowLocation[]>(() =>
		sourceLocations?.length ? sourceLocations.map(cloneLocation) : [],
	);
	const locationsRef = useRef(locations);
	const lastSyncedRevisionRef = useRef<null | number | string>(null);

	const [selectedLocationId, setSelectedLocationId] = useState<null | string>(
		() => focusedLocationId ?? null,
	);
	const selectedLocationIdRef = useRef(selectedLocationId);

	// Keep latest values for async callbacks without reading/writing refs during render.
	useEffect(() => {
		onLocationsMutatedRef.current = onLocationsMutated;
		onLocationSelectedRef.current = onLocationSelected;
		onLocationClusterSelectRef.current = onLocationClusterSelect;
		onMapFocusChangeRef.current = onMapFocusChange;
		locationsRef.current = locations;
		selectedLocationIdRef.current = selectedLocationId;
	});

	const isLocationSelected = selectedLocationId !== null;

	const location = useMemo(() => {
		if (selectedLocationId) {
			const selected = locations.find(
				(entry) => entry.id === selectedLocationId,
			);
			if (selected) return selected;
		}
		// Overview mode ignores this value; keep a stable Location for typed helpers.
		return locations[0] ?? EMPTY_MAP_LOCATION;
	}, [locations, selectedLocationId]);

	const setLocation = useCallback(
		(
			updater: ((current: ShadowLocation) => ShadowLocation) | ShadowLocation,
		) => {
			setLocations((current) => {
				const id = selectedLocationIdRef.current;
				if (!id) return current;
				return current.map((entry) => {
					if (entry.id !== id) return entry;
					return typeof updater === "function" ? updater(entry) : updater;
				});
			});
		},
		[],
	);

	const [isUpdatedSensorPending, setIsUpdatedSensorPending] = useState(false);
	const [isUpdatedZonePending, setIsUpdatedZonePending] = useState(false);

	const applySensorUpdate = useCallback(
		(
			args: {
				sensorData: Partial<ShadowSensor>;
				sensorId: string;
				silent?: boolean;
			},
			callbacks?: { onError?: () => void; onSuccess?: () => void },
		) => {
			const { sensorData, sensorId } = args;
			const previousLocations = locationsRef.current;
			setIsUpdatedSensorPending(true);

			setLocation((current) =>
				updateSensorInLocation(
					current,
					sensorId,
					(sensor) => ({
						...sensor,
						...sensorData,
						status: (sensorData.status ?? sensor.status) as SensorStatus,
					}),
					getRefId(sensorData.zone),
				),
			);

			const body = toShadowSensorUpdate(sensorData);
			void sensorsApi
				.updateSensor(sensorId, body)
				.then(() => {
					onLocationsMutatedRef.current?.();
					callbacks?.onSuccess?.();
				})
				.catch(() => {
					setLocations(previousLocations.map(cloneLocation));
					callbacks?.onError?.();
				})
				.finally(() => {
					setIsUpdatedSensorPending(false);
				});
		},
		[setLocation],
	);

	const updateSensor = applySensorUpdate;
	const updateSensorAsync = useCallback(
		async (args: {
			sensorData: Partial<ShadowSensor>;
			sensorId: string;
			silent?: boolean;
		}) => {
			const { sensorData, sensorId } = args;
			const previousLocations = locationsRef.current;
			setIsUpdatedSensorPending(true);

			setLocation((current) =>
				updateSensorInLocation(
					current,
					sensorId,
					(sensor) => ({
						...sensor,
						...sensorData,
						status: (sensorData.status ?? sensor.status) as SensorStatus,
					}),
					getRefId(sensorData.zone),
				),
			);

			try {
				await sensorsApi.updateSensor(
					sensorId,
					toShadowSensorUpdate(sensorData),
				);
				onLocationsMutatedRef.current?.();
			} catch (error) {
				setLocations(previousLocations.map(cloneLocation));
				throw error;
			} finally {
				setIsUpdatedSensorPending(false);
			}
		},
		[setLocation],
	);

	const applyZoneUpdate = useCallback(
		(
			args: {
				silent?: boolean;
				zoneData: Parameters<typeof mergeZonePatch>[1];
				zoneId: string;
			},
			callbacks?: { onError?: () => void; onSuccess?: () => void },
		) => {
			const { zoneData, zoneId } = args;
			const previousLocations = locationsRef.current;
			setIsUpdatedZonePending(true);

			setLocation((current) =>
				updateZoneInLocation(current, zoneId, (zone) =>
					mergeZonePatch(zone, zoneData),
				),
			);

			void zonesApi
				.updateZone(zoneId, toShadowZoneUpdate(zoneData))
				.then(() => {
					onLocationsMutatedRef.current?.();
					callbacks?.onSuccess?.();
				})
				.catch(() => {
					setLocations(previousLocations.map(cloneLocation));
					callbacks?.onError?.();
				})
				.finally(() => {
					setIsUpdatedZonePending(false);
				});
		},
		[setLocation],
	);

	const updateZone = applyZoneUpdate;

	const zones = useMemo(
		() =>
			isLocationSelected
				? sortZonesForMapList(location.zones ?? EMPTY_ZONES)
				: EMPTY_ZONES,
		[isLocationSelected, location.zones],
	);

	const mapDataFingerprint = useMemo(
		() =>
			isLocationSelected
				? getLocationMapDataFingerprint(location)
				: getLocationsMapDataFingerprint(locations),
		[isLocationSelected, location, locations],
	);

	const locationClusterMarkers = useMemo(
		() =>
			buildLocationClusterMarkers(locations, {
				locationIdsWithInstallation,
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- locations read when fingerprint changes
		[mapDataFingerprint, locationIdsWithInstallation],
	);

	const locationZoneClusterMarkers = useMemo(
		() => (isLocationSelected ? buildLocationZoneClusterMarkers(location) : []),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- location read when fingerprint changes
		[isLocationSelected, mapDataFingerprint],
	);

	const locationSensorMarkers = useMemo(
		() => (isLocationSelected ? buildLocationMapMarkers(location) : []),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- location read when fingerprint changes
		[isLocationSelected, mapDataFingerprint],
	);

	const { mappable: mappableLocations, unmapped: unmappedLocations } = useMemo(
		() => partitionLocations(locations),
		[locations],
	);

	const mappableLocationCount = mappableLocations.length;
	const unmappedLocationCount = unmappedLocations.length;

	const zonePanelIndexById = useMemo(
		() => new Map(zones.map((zone, index) => [zone.id, index])),
		[zones],
	);

	const [openZoneIds, setOpenZoneIds] = useState<Set<string>>(() => new Set());
	const [highlightedFloorId, setHighlightedFloorId] = useState<null | string>(
		null,
	);
	const [isFloorEditActive, setFloorEditActive] = useState(false);
	const [hoveredSensorId, setHoveredSensorId] = useState<string | undefined>();
	const [hoveredLocationId, setHoveredLocationId] = useState<
		string | undefined
	>();
	const [activeFeature, setActiveFeature] = useState<
		GeoJSON.Feature | undefined
	>();
	const [editingSensorId, setEditingSensorId] = useState<null | string>(null);
	const [editDraft, setEditDraft] = useState<null | ShadowSensorEditDraft>(
		null,
	);
	const [editingZoneId, setEditingZoneId] = useState<null | string>(null);
	const [zoneEditDraft, setZoneEditDraft] =
		useState<null | ShadowZoneEditDraft>(null);
	const [moveSensorsWithOutline, setMoveSensorsWithOutline] = useState(true);
	const [zoneSensorCoordinates, setZoneSensorCoordinates] =
		useState<SensorLngLatById>({});
	const [lineBoundariesByZoneId, setLineBoundariesByZoneId] = useState<
		Record<string, GeoJSONLineString>
	>({});

	const editingSensorZoneId = editingSensorId
		? (editDraft?.zoneId ??
			findSensorInLocation(location, editingSensorId)?.zoneId ??
			null)
		: null;
	const openZoneId = openZoneIds.size > 0 ? [...openZoneIds][0]! : null;
	const floorScope = useMemo(() => {
		if (!isLocationSelected) return null;

		const hasSelection = Boolean(
			editingZoneId || editingSensorZoneId || openZoneId || highlightedFloorId,
		);
		const urlFocus = hasSelection
			? null
			: resolveLocationMapFocus(
					urlMapFocus ?? {},
					toLocationMapFocusEntities([location]),
				);

		return resolveMapFloorScope({
			editingSensorZoneId,
			editingZoneId,
			floors: location.floors ?? [],
			highlightedFloorId: highlightedFloorId ?? urlFocus?.floorId,
			openZoneId: openZoneId ?? urlFocus?.zoneId,
			zones: location.zones ?? [],
		});
	}, [
		editingSensorZoneId,
		editingZoneId,
		highlightedFloorId,
		isLocationSelected,
		location,
		openZoneId,
		urlMapFocus,
	]);
	const visibleFloorId = floorScope?.floorId ?? null;

	/**
	 * Match dashboard drill: location overview → zone cluster cards; expand a
	 * zone (or edit) → individual sensor pins. A selected floor shows that
	 * floor's sensor pins directly so the other floors can be hidden.
	 */
	const showZoneClusterMarkers =
		isLocationSelected &&
		openZoneIds.size === 0 &&
		!editingSensorId &&
		!editingZoneId &&
		!floorScope;

	const mapMarkers = useMemo(() => {
		if (!isLocationSelected) return locationClusterMarkers;
		if (showZoneClusterMarkers) return locationZoneClusterMarkers;
		return locationSensorMarkers;
	}, [
		isLocationSelected,
		locationClusterMarkers,
		locationSensorMarkers,
		locationZoneClusterMarkers,
		showZoneClusterMarkers,
	]);

	// Sync from API when the org list loads or React Query refetches after a
	// mutate. First sync (and org swap) resets UI; later revisions only refresh
	// location data so zone/sensor edit sessions survive autosave.
	useEffect(() => {
		if (sourceRevision == null || sourceLocations == null) return;
		if (lastSyncedRevisionRef.current === sourceRevision) return;

		const previousRevision = lastSyncedRevisionRef.current;
		lastSyncedRevisionRef.current = sourceRevision;

		setLocations(sourceLocations.map(cloneLocation));

		const previousOrg =
			typeof previousRevision === "string"
				? previousRevision.split(":")[0]
				: null;
		const nextOrg =
			typeof sourceRevision === "string" ? sourceRevision.split(":")[0] : null;
		const previousFocusKey =
			typeof previousRevision === "string"
				? (previousRevision.split(":")[2] ?? "all")
				: "all";
		const nextFocusKey =
			typeof sourceRevision === "string"
				? (sourceRevision.split(":")[2] ?? "all")
				: "all";
		const isFirstSync = previousRevision == null;
		const isOrgChange =
			previousOrg != null && nextOrg != null && previousOrg !== nextOrg;
		// Single-location ↔ all-locations (or A → B) route changes share an org
		// and would otherwise look like a soft refresh that keeps the old selection.
		const isFocusScopeChange = previousFocusKey !== nextFocusKey;

		setSelectedLocationId((current) => {
			if (focusedLocationId) {
				return sourceLocations.some((entry) => entry.id === focusedLocationId)
					? focusedLocationId
					: null;
			}
			if (isFocusScopeChange) {
				return null;
			}
			if (!current) return null;
			return sourceLocations.some((entry) => entry.id === current)
				? current
				: null;
		});

		// Soft refresh after autosave / mutate — keep accordion + edit session.
		if (!isFirstSync && !isOrgChange && !isFocusScopeChange) {
			return;
		}

		setOpenZoneIds(new Set());
		setHighlightedFloorId(null);
		setEditingSensorId(null);
		setEditDraft(null);
		setEditingZoneId(null);
		setZoneEditDraft(null);
		setActiveFeature(undefined);
		setHoveredSensorId(undefined);
		setHoveredLocationId(undefined);
		setZoneSensorCoordinates({});
		setMoveSensorsWithOutline(true);
		setLineBoundariesByZoneId({});
	}, [focusedLocationId, sourceLocations, sourceRevision]);

	const mapOverlays = useMemo(() => {
		if (!isLocationSelected) return undefined;

		const overlays =
			buildLocationMapOverlays(location, {
				excludeZoneIds: editingZoneId ? new Set([editingZoneId]) : undefined,
				lineBoundaries: lineBoundariesByZoneId,
				zoneBoundsShape,
			}) ?? [];
		const floorOutlines = buildFloorBoundaryOverlays(
			location.floors ?? [],
			location.zones ?? [],
		);

		if (
			editingZoneId &&
			zoneEditDraft?.boundaryKind !== "line" &&
			zoneEditDraft?.boundaryMode === "auto"
		) {
			const zone = location.zones?.find((entry) => entry.id === editingZoneId);
			const autoPolygon = zone
				? buildAutoZoneDrawPolygon({
						location,
						sensorCoordinates: zoneSensorCoordinates,
						zone,
						zoneBoundsShape,
					})
				: null;
			if (autoPolygon) {
				const preview = buildZoneBoundsFeature({
					coordinates: [],
					fallbackToDerived: false,
					storedBoundary: autoPolygon,
				});
				if (preview) {
					overlays.push(
						toZoneBoundsOverlay(
							editingZoneId,
							preview,
							zoneBoundsShape,
							zoneEditDraft.color ?? zone?.color,
							normalizeZoneBorderWidth(zoneEditDraft.borderWidth),
						),
					);
				}
			}
		}

		if (
			editingZoneId &&
			zoneEditDraft?.boundaryKind !== "line" &&
			zoneEditDraft?.boundaryMode === "custom" &&
			zoneEditDraft.boundary
		) {
			const preview = buildZoneBoundsFeature({
				coordinates: [],
				storedBoundary: zoneEditDraft.boundary,
			});
			if (preview) {
				const zone = location.zones?.find(
					(entry) => entry.id === editingZoneId,
				);
				overlays.push(
					toZoneBoundsOverlay(
						editingZoneId,
						preview,
						zoneBoundsShape,
						zoneEditDraft.color ?? zone?.color,
						normalizeZoneBorderWidth(zoneEditDraft.borderWidth),
					),
				);
			}
		}

		return overlays.length > 0 || floorOutlines.length > 0
			? [...floorOutlines, ...overlays]
			: undefined;
		// eslint-disable-next-line react-hooks/exhaustive-deps -- location read from latest render when fingerprint changes
	}, [
		editingZoneId,
		isLocationSelected,
		location,
		lineBoundariesByZoneId,
		mapDataFingerprint,
		zoneBoundsShape,
		zoneEditDraft?.boundary,
		zoneEditDraft?.boundaryKind,
		zoneEditDraft?.boundaryMode,
		zoneEditDraft?.borderWidth,
		zoneEditDraft?.color,
		zoneSensorCoordinates,
	]);

	const isEditing = editingSensorId !== null || editingZoneId !== null;

	const editingSensorContext = useMemo(
		() =>
			editingSensorId
				? findSensorInLocation(location, editingSensorId)
				: undefined,
		[editingSensorId, location],
	);

	const mapBreadcrumbs = useMemo(() => {
		if (!isLocationSelected) return [];

		const selectedSensorId = editingSensorId
			? editingSensorId
			: activeFeature?.id && isSensorMarkerFeature(activeFeature)
				? String(activeFeature.id)
				: undefined;
		const selectedSensorContext = selectedSensorId
			? findSensorInLocation(location, selectedSensorId)
			: undefined;

		const floors = location.floors ?? [];
		let focusedZone: ShadowZone | undefined;
		let focusedFloor: (typeof floors)[number] | undefined;

		if (editingZoneId) {
			focusedZone = zones.find((zone) => zone.id === editingZoneId);
		} else if (selectedSensorContext) {
			focusedZone = zones.find(
				(zone) => zone.id === selectedSensorContext.zoneId,
			);
		} else if (highlightedFloorId) {
			focusedFloor = floors.find((floor) => floor.id === highlightedFloorId);
		} else if (openZoneIds.size === 1) {
			const [zoneId] = openZoneIds;
			focusedZone = zones.find((zone) => zone.id === zoneId);
		}

		if (focusedZone) {
			const floorId = getRefId(focusedZone.floor);
			focusedFloor = floorId
				? floors.find((floor) => floor.id === floorId)
				: undefined;
		}

		const locationCrumbs = buildLocationMapBreadcrumbs({
			floors,
			focusedFloor,
			focusedZone,
			location,
			selectedSensor: selectedSensorContext?.sensor,
		});

		const allLocationsCrumb: MapDrillBreadcrumb = {
			id: "all-locations",
			label: "All Locations",
			level: "locations",
		};

		if (locationCrumbs.length > 0) {
			return [allLocationsCrumb, ...locationCrumbs];
		}

		return [
			allLocationsCrumb,
			{
				...(floors.length > 0
					? {
							floorOptions: floors.map((floor) => ({
								id: floor.id,
								label: toTitleCase(floor.name),
							})),
						}
					: {}),
				id: `location:${location.id}`,
				label: toTitleCase(location.name),
				level: "zones",
				locationId: location.id,
				locationName: location.name,
			},
		];
	}, [
		activeFeature,
		editingSensorId,
		editingZoneId,
		highlightedFloorId,
		isLocationSelected,
		location,
		openZoneIds,
		zones,
	]);

	const editZoneOptions = useMemo(() => {
		if (!editingSensorContext) return [];

		const { sensor, zoneId: currentZoneId } = editingSensorContext;
		const sensorInstallationId = getRefId(sensor.installation);

		const options = zones
			.filter((zone) => {
				if (!sensorInstallationId) return true;
				return getRefId(zone.installation) === sensorInstallationId;
			})
			.map((zone) => ({
				id: zone.id,
				label: toTitleCase(zone.name),
			}));

		if (
			currentZoneId &&
			!options.some((option) => option.id === currentZoneId)
		) {
			const currentZone = zones.find((zone) => zone.id === currentZoneId);
			options.unshift({
				id: currentZoneId,
				label: currentZone ? toTitleCase(currentZone.name) : "Current zone",
			});
		}

		return options;
	}, [editingSensorContext, zones]);

	const editTypeOptions = useMemo(() => {
		if (!editingSensorContext) return [];

		const options: { id: string; label: string }[] = [...SENSOR_TYPE_OPTIONS];
		const { sensor } = editingSensorContext;

		if (sensor.type && !options.some((option) => option.id === sensor.type)) {
			options.push({
				id: sensor.type,
				label: toTitleCase(sensor.type),
			});
		}

		return options;
	}, [editingSensorContext]);

	const pendingActiveSensorIdRef = useRef<null | string>(null);
	const lastSavedDraftFingerprintRef = useRef<null | string>(null);
	const confirmedSavedFingerprintRef = useRef<null | string>(null);
	const persistInFlightFingerprintRef = useRef<null | string>(null);
	const hasSavedThisSessionRef = useRef(false);
	const editDraftRef = useRef(editDraft);
	const editingSensorContextRef = useRef(editingSensorContext);
	const persistSensorDraftRef = useRef<
		(options?: PersistSensorDraftOptions) => boolean
	>(() => false);
	const draftHistoryRef = useRef<ShadowSensorEditDraft[]>([]);
	const initialDraftRef = useRef<null | ShadowSensorEditDraft>(null);
	const lastSavedDraftRef = useRef<null | ShadowSensorEditDraft>(null);
	const [canUndoSensorEdit, setCanUndoSensorEdit] = useState(false);

	const zoneEditDraftRef = useRef(zoneEditDraft);
	const editingZoneRef = useRef<ShadowZone | undefined>(undefined);
	const persistZoneDraftRef = useRef<
		(options?: PersistZoneDraftOptions) => boolean
	>(() => false);
	const zoneDraftHistoryRef = useRef<ZoneEditHistoryEntry[]>([]);
	const zoneInitialDraftRef = useRef<null | ShadowZoneEditDraft>(null);
	const zoneLastSavedDraftRef = useRef<null | ShadowZoneEditDraft>(null);
	const captureZoneBoundaryRef = useRef<(() => null | ZoneBoundary) | null>(
		null,
	);
	const registerZoneBoundaryCapture = useCallback(
		(capture: (() => null | ZoneBoundary) | null) => {
			captureZoneBoundaryRef.current = capture;
		},
		[],
	);
	const zoneSensorCoordinatesRef = useRef<SensorLngLatById>({});
	const zoneInitialSensorCoordinatesRef = useRef<SensorLngLatById>({});
	const zoneLastSavedSensorCoordinatesRef = useRef<SensorLngLatById>({});
	const moveSensorsWithOutlineRef = useRef(true);
	const autoZoneDrawPolygonRef = useRef<GeoJSONPolygon | null>(null);
	const zoneMovedSensorsPersistInFlightRef = useRef(false);
	const zoneMovedSensorsLatestRef = useRef<SensorLngLatById>({});
	const zoneLastSavedFingerprintRef = useRef<null | string>(null);
	const zoneConfirmedSavedFingerprintRef = useRef<null | string>(null);
	const zonePersistInFlightFingerprintRef = useRef<null | string>(null);
	const zoneHasSavedThisSessionRef = useRef(false);
	const [canUndoZoneEdit, setCanUndoZoneEdit] = useState(false);

	const [lastSavedFingerprint, setLastSavedFingerprint] = useState<
		null | string
	>(null);
	const [hasSavedThisSession, setHasSavedThisSession] = useState(false);
	const [zoneLastSavedFingerprint, setZoneLastSavedFingerprint] = useState<
		null | string
	>(null);
	const [zoneHasSavedThisSession, setZoneHasSavedThisSession] = useState(false);

	const mapOverlaysWithPendingZoneColors = useMemo(() => {
		if (!mapOverlays || !editingZoneId || !zoneEditDraft) return mapOverlays;

		const pendingColor = normalizeZoneColor(zoneEditDraft.color);
		return mapOverlays.map((overlay) => {
			if (getOverlayZoneId(overlay) !== editingZoneId) return overlay;

			return {
				...overlay,
				properties: {
					...overlay.properties,
					zoneColor: pendingColor,
				},
			};
		});
	}, [editingZoneId, mapOverlays, zoneEditDraft]);

	const openPanels = useMemo(() => {
		const indices = new Set<number>();
		zones.forEach((zone, index) => {
			if (openZoneIds.has(zone.id)) {
				indices.add(index);
			}
		});
		return indices;
	}, [openZoneIds, zones]);

	const mapFocusFitKey = useMemo(() => {
		if (!isLocationSelected) {
			// Include geometry fingerprint so org switches (and late-loaded
			// cached lists) reframe the overview; soft refetches with the same
			// coords keep the camera still.
			return `locations-overview|${mapDataFingerprint}`;
		}

		if (editingSensorId) {
			return `location:${selectedLocationId}|sensor:${editingSensorId}`;
		}

		// Distinct from open-panel focus so entering edit always reframes.
		if (editingZoneId) {
			return `location:${selectedLocationId}|edit-zone:${editingZoneId}`;
		}

		if (activeFeature?.id) {
			return `location:${selectedLocationId}|sensor:${String(activeFeature.id)}`;
		}

		if (openZoneIds.size > 0) {
			return `location:${selectedLocationId}|${[...openZoneIds].sort().join("|")}`;
		}

		if (visibleFloorId) {
			return `location:${selectedLocationId}|floor:${visibleFloorId}`;
		}

		return `location:${selectedLocationId}|all`;
	}, [
		activeFeature,
		editingSensorId,
		editingZoneId,
		isLocationSelected,
		mapDataFingerprint,
		openZoneIds,
		selectedLocationId,
		visibleFloorId,
	]);

	useEffect(() => {
		const pendingSensorId = pendingActiveSensorIdRef.current;

		if (pendingSensorId) {
			const marker = mapMarkers.find(
				(feature) => String(feature.id ?? "") === pendingSensorId,
			);
			if (!marker) return;

			setActiveFeature(marker);
			setHoveredSensorId(pendingSensorId);
			pendingActiveSensorIdRef.current = null;
			return;
		}

		setActiveFeature((current) => {
			if (!current?.id) return current;

			const marker = mapMarkers.find(
				(feature) => String(feature.id ?? "") === String(current.id),
			);
			return marker ?? current;
		});
	}, [mapMarkers]);

	useEffect(() => {
		onInteractionChange?.(isEditing);
	}, [isEditing, onInteractionChange]);

	const highlightedFloorZoneIds = useMemo((): null | Set<string> => {
		if (!highlightedFloorId) return null;
		if (
			!(location.floors ?? []).some((floor) => floor.id === highlightedFloorId)
		) {
			return null;
		}

		const ids = new Set<string>();
		for (const zone of location.zones ?? []) {
			if (getRefId(zone.floor) === highlightedFloorId) ids.add(zone.id);
		}
		return ids;
	}, [highlightedFloorId, location.floors, location.zones]);

	const focusedZoneIds = useMemo((): null | Set<string> => {
		if (editingSensorId && editDraft?.zoneId) {
			return new Set([editDraft.zoneId]);
		}

		if (editingZoneId) {
			return new Set([editingZoneId]);
		}

		if (highlightedFloorZoneIds && !floorScope) return highlightedFloorZoneIds;

		if (floorScope && !editingZoneId && !editingSensorId) {
			return new Set(floorScope.zoneIds);
		}

		if (openZoneIds.size === 0) return null;

		return openZoneIds;
	}, [
		editDraft,
		editingSensorId,
		editingZoneId,
		highlightedFloorZoneIds,
		openZoneIds,
		floorScope,
	]);

	const activeHighlightedFloorId = visibleFloorId;

	const scopeMapFeatures = (
		features: GeoJSON.Feature[] | undefined,
		getZoneId: (feature: GeoJSON.Feature) => string | undefined,
	) => {
		if (!features || !floorScope) return features;
		return features.filter((feature) => {
			if (feature.properties?.overlayKind === "floor") {
				return feature.properties.floorId === floorScope.floorId;
			}
			const zoneId = getZoneId(feature);
			return zoneId !== undefined && floorScope.zoneIds.has(zoneId);
		});
	};

	const visibleMapMarkers = useMemo(
		() =>
			focusMapFeaturesByZones(
				scopeMapFeatures(mapMarkers, getMarkerZoneId),
				focusedZoneIds,
				getMarkerZoneId,
				dimUnfocusedZones,
				activeHighlightedFloorId,
			) ?? [],
		// scopeMapFeatures closes over floorScope
		// eslint-disable-next-line react-hooks/exhaustive-deps -- floorScope is the scope input
		[
			activeHighlightedFloorId,
			dimUnfocusedZones,
			floorScope,
			focusedZoneIds,
			mapMarkers,
		],
	);

	const visibleMapOverlays = useMemo(() => {
		if (!mapOverlaysWithPendingZoneColors) return undefined;

		const overlays =
			focusMapFeaturesByZones(
				scopeMapFeatures(mapOverlaysWithPendingZoneColors, getOverlayZoneId),
				focusedZoneIds,
				getOverlayZoneId,
				dimUnfocusedZones,
				activeHighlightedFloorId,
			) ?? [];

		return overlays.length > 0 ? overlays : undefined;
		// eslint-disable-next-line react-hooks/exhaustive-deps -- floorScope is the scope input
	}, [
		activeHighlightedFloorId,
		dimUnfocusedZones,
		floorScope,
		focusedZoneIds,
		mapOverlaysWithPendingZoneColors,
	]);

	const showEmptyFocusedZoneMessage =
		focusedZoneIds !== null &&
		visibleMapMarkers.length === 0 &&
		!(visibleFloorId && openZoneIds.size === 0);

	const displayMapMarkers = useMemo(() => {
		const markersWithZoneMove = editingZoneId
			? applySensorCoordinateOverridesToMarkers(
					visibleMapMarkers,
					zoneSensorCoordinates,
				)
			: visibleMapMarkers;

		if (!editingSensorId || !editDraft) return markersWithZoneMove;

		const editingContext = findSensorInLocation(location, editingSensorId);
		if (!editingContext) return markersWithZoneMove;

		const { sensor } = editingContext;
		const editCoordinates = parseSensorEditDraftCoordinates(editDraft);

		const otherMarkers = markersWithZoneMove
			.filter((marker) => marker.id !== editingSensorId)
			.map((marker) => ({
				...marker,
				properties: {
					...marker.properties,
					dimmed: true,
					editable: false,
				},
			}));

		// No coordinates yet: wait for map click (or typed lat/lng) before showing a pin.
		if (!editCoordinates) {
			return otherMarkers;
		}

		const editingMarker = createSensorMarkerFeature({
			coordinates: editCoordinates,
			id: sensor.id,
			lastInputReceived: sensor.lastInputReceived ?? null,
			locationId: location.id,
			name: editDraft.name,
			status: editDraft.status,
			trueCoordinates: editCoordinates,
			type: editDraft.type,
			zoneId: editDraft.zoneId,
		});

		return [
			...otherMarkers,
			{
				...editingMarker,
				properties: {
					...editingMarker.properties,
					dimmed: false,
					editable: true,
				},
			},
		];
	}, [
		editDraft,
		editingSensorId,
		editingZoneId,
		location,
		visibleMapMarkers,
		zoneSensorCoordinates,
	]);

	const emphasizedSensorIds = useMemo((): ReadonlySet<string> | undefined => {
		const ids = new Set<string>();
		const isOnMap = (sensorId: string) =>
			displayMapMarkers.some((marker) => String(marker.id) === sensorId);

		if (activeFeature?.id && isSensorMarkerFeature(activeFeature)) {
			const activeId = String(activeFeature.id);
			if (isOnMap(activeId)) ids.add(activeId);
		}

		if (editingSensorId && isOnMap(editingSensorId)) {
			ids.add(editingSensorId);
		}

		if (
			hoveredSensorId &&
			isOnMap(hoveredSensorId) &&
			(!isEditing || editingZoneId)
		) {
			ids.add(hoveredSensorId);
		}

		return ids.size > 0 ? ids : undefined;
	}, [
		activeFeature,
		displayMapMarkers,
		editingSensorId,
		editingZoneId,
		hoveredSensorId,
		isEditing,
	]);

	/** Location-cluster feature ids to scale up while hovering the sidebar list. */
	const emphasizedLocationClusterIds = useMemo(():
		| ReadonlySet<string>
		| undefined => {
		if (isLocationSelected || !hoveredLocationId) return undefined;
		const clusterId = `location-cluster:${hoveredLocationId}`;
		const onMap = displayMapMarkers.some(
			(marker) => String(marker.id) === clusterId,
		);
		return onMap ? new Set([clusterId]) : undefined;
	}, [displayMapMarkers, hoveredLocationId, isLocationSelected]);

	// Frame a single pin when selecting or editing a sensor; otherwise fit the
	// focused / editing zone's overlay (fallback to that zone's pins).
	const fitMapMarkers = useMemo(() => {
		if (editingSensorId) {
			const editingMarker = displayMapMarkers.find(
				(marker) => marker.id === editingSensorId,
			);
			if (editingMarker) return [editingMarker];
		}

		const fitToZoneIds = (zoneIds: Set<string>): GeoJSON.Feature[] => {
			const zoneOverlays = (mapOverlaysWithPendingZoneColors ?? []).filter(
				(overlay) => {
					const zoneId = getOverlayZoneId(overlay);
					return zoneId != null && zoneIds.has(zoneId);
				},
			);
			const overlayCoords = zoneOverlays.flatMap(coordinatesFromMapFitFeature);
			const uniqueOverlayPoints = new Set(
				overlayCoords.map(([lng, lat]) => `${lng},${lat}`),
			);
			if (uniqueOverlayPoints.size >= 2) {
				return zoneOverlays;
			}

			// Always fall back to sensor pins — never zone-cluster cards — so a
			// focused zone frames its geometry instead of a single DEFAULT_ZOOM point.
			return (
				filterMapFeaturesByZones(
					locationSensorMarkers,
					zoneIds,
					getMarkerZoneId,
				) ?? []
			);
		};

		if (editingZoneId) {
			return fitToZoneIds(new Set([editingZoneId]));
		}

		if (activeFeature?.id) {
			const marker = mapMarkers.find(
				(feature) => feature.id === activeFeature.id,
			);
			if (marker) return [marker];
		}

		if (openZoneIds.size > 0) {
			return fitToZoneIds(openZoneIds);
		}

		if (visibleFloorId && focusedZoneIds) {
			const fitted = fitToZoneIds(focusedZoneIds);
			if (fitted.length > 0) return fitted;

			const floorOutline = (mapOverlaysWithPendingZoneColors ?? []).filter(
				(overlay) =>
					overlay.properties?.overlayKind === "floor" &&
					overlay.properties?.floorId === visibleFloorId,
			);
			const outlineCoords = floorOutline.flatMap(coordinatesFromMapFitFeature);
			if (outlineCoords.length >= 2) return floorOutline;

			return fitted;
		}

		if (focusedZoneIds) {
			return fitToZoneIds(focusedZoneIds);
		}

		return [
			...mapMarkers,
			...buildFloorBoundaryOverlays(
				location.floors ?? [],
				location.zones ?? [],
			),
		];
	}, [
		activeFeature,
		displayMapMarkers,
		editingSensorId,
		editingZoneId,
		focusedZoneIds,
		location.floors,
		location.zones,
		locationSensorMarkers,
		mapMarkers,
		mapOverlaysWithPendingZoneColors,
		openZoneIds,
		visibleFloorId,
	]);

	const visibleMapCenter = useMemo(() => {
		const markersForCenter =
			fitMapMarkers.length > 0 ? fitMapMarkers : mapMarkers;

		if (!isLocationSelected) {
			const firstPoint = markersForCenter.find(
				(feature) => feature.geometry.type === "Point",
			);
			if (firstPoint?.geometry.type === "Point") {
				const coordinates = firstPoint.geometry.coordinates;
				if (
					Array.isArray(coordinates) &&
					typeof coordinates[0] === "number" &&
					typeof coordinates[1] === "number"
				) {
					return [coordinates[0], coordinates[1]] as [number, number];
				}
			}
			return undefined;
		}

		return getLocationMapCenter(location, markersForCenter);
	}, [fitMapMarkers, isLocationSelected, location, mapMarkers]);

	const isEditDirty = useMemo(() => {
		if (!editingSensorContext || !editDraft) return false;

		const { sensor, zoneId } = editingSensorContext;
		const savedCoordinateStrings = getCoordinateStringsFromSensor(sensor);

		return (
			editDraft.name.trim() !== sensor.name.trim() ||
			editDraft.type !== sensor.type ||
			editDraft.zoneId !== zoneId ||
			editDraft.latitude.trim() !== savedCoordinateStrings.latitude.trim() ||
			editDraft.longitude.trim() !== savedCoordinateStrings.longitude.trim()
		);
	}, [editDraft, editingSensorContext]);

	const clearZoneDraftHistory = useCallback(() => {
		zoneDraftHistoryRef.current = [];
		setCanUndoZoneEdit(false);
	}, []);

	const clearZoneInitialDraft = useCallback(() => {
		zoneInitialDraftRef.current = null;
	}, []);

	const discardZoneEdit = useCallback(() => {
		setEditingZoneId(null);
		setZoneEditDraft(null);
		setMoveSensorsWithOutline(true);
		setZoneSensorCoordinates({});
		zoneSensorCoordinatesRef.current = {};
		zoneInitialSensorCoordinatesRef.current = {};
		zoneLastSavedSensorCoordinatesRef.current = {};
		setZoneLastSavedFingerprint(null);
		setZoneHasSavedThisSession(false);
		zoneLastSavedFingerprintRef.current = null;
		zoneConfirmedSavedFingerprintRef.current = null;
		zonePersistInFlightFingerprintRef.current = null;
		zoneHasSavedThisSessionRef.current = false;
		zoneLastSavedDraftRef.current = null;
		clearZoneDraftHistory();
		clearZoneInitialDraft();
	}, [clearZoneDraftHistory, clearZoneInitialDraft]);

	const clearDraftHistory = useCallback(() => {
		draftHistoryRef.current = [];
		setCanUndoSensorEdit(false);
	}, []);

	const clearInitialDraft = useCallback(() => {
		initialDraftRef.current = null;
	}, []);

	const discardSensorEdit = useCallback(() => {
		setEditingSensorId(null);
		setEditDraft(null);
		setActiveFeature(undefined);
		setLastSavedFingerprint(null);
		setHasSavedThisSession(false);
		pendingActiveSensorIdRef.current = null;
		lastSavedDraftFingerprintRef.current = null;
		lastSavedDraftRef.current = null;
		confirmedSavedFingerprintRef.current = null;
		persistInFlightFingerprintRef.current = null;
		hasSavedThisSessionRef.current = false;
		clearDraftHistory();
		clearInitialDraft();
	}, [clearDraftHistory, clearInitialDraft]);

	const rememberSavedFingerprint = useCallback((fingerprint: string) => {
		lastSavedDraftFingerprintRef.current = fingerprint;
		confirmedSavedFingerprintRef.current = fingerprint;
		setLastSavedFingerprint(fingerprint);
	}, []);

	const finishSensorEdit = useCallback(
		(sensorId: string, zoneId: string) => {
			setEditingSensorId(null);
			setEditDraft(null);
			setOpenZoneIds(new Set([zoneId]));
			setLastSavedFingerprint(null);
			setHasSavedThisSession(false);
			pendingActiveSensorIdRef.current = sensorId;
			lastSavedDraftFingerprintRef.current = null;
			lastSavedDraftRef.current = null;
			confirmedSavedFingerprintRef.current = null;
			persistInFlightFingerprintRef.current = null;
			hasSavedThisSessionRef.current = false;
			clearDraftHistory();
			clearInitialDraft();

			const marker = mapMarkers.find((feature) => feature.id === sensorId);
			if (marker) {
				setActiveFeature(marker);
			}
		},
		[clearDraftHistory, clearInitialDraft, mapMarkers],
	);

	const persistSensorDraft = useCallback(
		(options?: PersistSensorDraftOptions): boolean => {
			if (!canCustomizeLocationMap) return false;

			const draft = editDraftRef.current;
			const context = editingSensorContextRef.current;
			if (!draft || !context) return false;
			if (!canPersistSensorEditDraft(draft)) return false;

			const fingerprint = getSensorEditDraftFingerprint(draft);
			if (fingerprint === lastSavedDraftFingerprintRef.current) {
				if (options?.closeOnSuccess) {
					finishSensorEdit(context.sensor.id, draft.zoneId);
				}
				return false;
			}

			if (persistInFlightFingerprintRef.current === fingerprint) {
				return false;
			}

			persistInFlightFingerprintRef.current = fingerprint;
			const checkpoint = lastSavedDraftRef.current;
			const skipUndoCheckpoint =
				options?.skipUndoCheckpoint || options?.closeOnSuccess;

			updateSensor(
				{
					sensorData: buildSensorUpdateFromEditDraft(draft),
					sensorId: context.sensor.id,
					silent: options?.silent ?? true,
				},
				{
					onError: () => {
						persistInFlightFingerprintRef.current = null;

						const stillEditingSameSensor =
							editingSensorContextRef.current?.sensor.id === context.sensor.id;
						if (!stillEditingSameSensor) return;

						lastSavedDraftFingerprintRef.current =
							confirmedSavedFingerprintRef.current;
						setLastSavedFingerprint(confirmedSavedFingerprintRef.current);
					},
					onSuccess: () => {
						persistInFlightFingerprintRef.current = null;

						const stillEditingSameSensor =
							editingSensorContextRef.current?.sensor.id === context.sensor.id;
						if (!stillEditingSameSensor) return;

						hasSavedThisSessionRef.current = true;
						setHasSavedThisSession(true);
						rememberSavedFingerprint(fingerprint);

						if (
							!skipUndoCheckpoint &&
							checkpoint &&
							getSensorEditDraftFingerprint(checkpoint) !== fingerprint
						) {
							draftHistoryRef.current.push({ ...checkpoint });
							setCanUndoSensorEdit(true);
						}

						lastSavedDraftRef.current = { ...draft };

						if (options?.closeOnSuccess) {
							finishSensorEdit(context.sensor.id, draft.zoneId);
							return;
						}

						const nextDraft = editDraftRef.current;
						if (
							nextDraft &&
							canPersistSensorEditDraft(nextDraft) &&
							getSensorEditDraftFingerprint(nextDraft) !==
								lastSavedDraftFingerprintRef.current
						) {
							persistSensorDraftRef.current({ silent: true });
						}
					},
				},
			);

			return true;
		},
		[
			canCustomizeLocationMap,
			finishSensorEdit,
			rememberSavedFingerprint,
			updateSensor,
		],
	);
	useEffect(() => {
		editDraftRef.current = editDraft;
		editingSensorContextRef.current = editingSensorContext;
		persistSensorDraftRef.current = persistSensorDraft;
	});

	const {
		cancel: cancelSensorAutoSave,
		flush: flushSensorAutoSave,
		schedule: scheduleSensorAutoSave,
	} = useDebouncedCallback(() => {
		persistSensorDraft({ silent: true });
	}, SENSOR_AUTO_SAVE_DELAY_MS);

	const editingZone = useMemo(
		() =>
			editingZoneId
				? zones.find((zone) => zone.id === editingZoneId)
				: undefined,
		[editingZoneId, zones],
	);

	const autoZoneDrawPolygon = useMemo(() => {
		if (
			!editingZone ||
			zoneEditDraft?.boundaryKind === "line" ||
			zoneEditDraft?.boundaryMode !== "auto"
		) {
			return null;
		}

		return buildAutoZoneDrawPolygon({
			location,
			sensorCoordinates: zoneSensorCoordinates,
			zone: editingZone,
			zoneBoundsShape,
		});
	}, [
		editingZone,
		location,
		zoneBoundsShape,
		zoneEditDraft?.boundaryKind,
		zoneEditDraft?.boundaryMode,
		zoneSensorCoordinates,
	]);

	const rememberZoneSavedFingerprint = useCallback((fingerprint: string) => {
		zoneLastSavedFingerprintRef.current = fingerprint;
		zoneConfirmedSavedFingerprintRef.current = fingerprint;
		setZoneLastSavedFingerprint(fingerprint);
	}, []);

	const finishZoneEdit = useCallback(
		(zoneId: string) => {
			setEditingZoneId(null);
			setZoneEditDraft(null);
			setMoveSensorsWithOutline(true);
			setZoneSensorCoordinates({});
			zoneSensorCoordinatesRef.current = {};
			zoneInitialSensorCoordinatesRef.current = {};
			zoneLastSavedSensorCoordinatesRef.current = {};
			setOpenZoneIds(new Set([zoneId]));
			setZoneLastSavedFingerprint(null);
			setZoneHasSavedThisSession(false);
			zoneLastSavedFingerprintRef.current = null;
			zoneConfirmedSavedFingerprintRef.current = null;
			zonePersistInFlightFingerprintRef.current = null;
			zoneHasSavedThisSessionRef.current = false;
			zoneLastSavedDraftRef.current = null;
			clearZoneDraftHistory();
			clearZoneInitialDraft();
		},
		[clearZoneDraftHistory, clearZoneInitialDraft],
	);

	const persistMovedZoneSensors = useCallback(
		async (target: SensorLngLatById) => {
			if (!canCustomizeLocationMap) return;

			zoneMovedSensorsLatestRef.current = target;
			setLocation((current) =>
				applySensorCoordinatesToLocation(current, target),
			);

			if (zoneMovedSensorsPersistInFlightRef.current) return;

			zoneMovedSensorsPersistInFlightRef.current = true;
			try {
				for (;;) {
					const latest = zoneMovedSensorsLatestRef.current;
					const previous = zoneLastSavedSensorCoordinatesRef.current;
					const changedEntries = Object.entries(latest).filter(
						([sensorId, coordinates]) => {
							const before = previous[sensorId];
							return (
								!before ||
								before[0] !== coordinates[0] ||
								before[1] !== coordinates[1]
							);
						},
					);

					if (changedEntries.length === 0) return;

					for (const [sensorId, coordinates] of changedEntries) {
						await updateSensorAsync({
							sensorData: {
								geolocation: {
									coordinates,
									type: "Point",
								},
							},
							sensorId,
							silent: true,
						});
					}

					zoneLastSavedSensorCoordinatesRef.current = { ...latest };
					if (latest === zoneMovedSensorsLatestRef.current) return;
				}
			} finally {
				zoneMovedSensorsPersistInFlightRef.current = false;
			}
		},
		[canCustomizeLocationMap, setLocation, updateSensorAsync],
	);

	const persistZoneDraft = useCallback(
		(options?: PersistZoneDraftOptions): boolean => {
			if (!canCustomizeLocationMap) return false;

			const zone = editingZoneRef.current;
			const currentDraft = zoneEditDraftRef.current;
			if (!currentDraft || !zone) return false;

			let draft: ShadowZoneEditDraft = currentDraft;

			if (draft.boundaryKind === "line") {
				if (draft.boundary !== null) {
					const captured = captureZoneBoundaryRef.current?.();
					const capturedLine = storedBoundaryToLineString(captured);
					const draftLine = storedBoundaryToLineString(draft.boundary);
					const line =
						(capturedLine?.coordinates.length ?? 0) >=
						(draftLine?.coordinates.length ?? 0)
							? (capturedLine ?? draftLine)
							: draftLine;
					if (line) {
						draft = {
							...draft,
							boundary: line,
							boundaryMode: "custom",
						};
						zoneEditDraftRef.current = draft;
						setZoneEditDraft(draft);
						setLineBoundariesByZoneId((current) => ({
							...current,
							[zone.id]: line,
						}));

						if (options?.closeOnSuccess) {
							const snapped = snapSensorLngLatByIdToLine(
								zoneSensorCoordinatesRef.current,
								line,
							);
							zoneSensorCoordinatesRef.current = snapped;
							setZoneSensorCoordinates(snapped);
						}
					}
				}
			}

			if (!canPersistZoneEditDraft(draft)) {
				return false;
			}

			const fingerprint = getZoneEditDraftFingerprint(draft);
			const sensorsChanged = !areSensorLngLatByIdEqual(
				zoneSensorCoordinatesRef.current,
				zoneLastSavedSensorCoordinatesRef.current,
			);

			if (fingerprint === zoneLastSavedFingerprintRef.current) {
				if (!sensorsChanged) {
					if (options?.closeOnSuccess) {
						finishZoneEdit(zone.id);
					}
					return false;
				}

				const skipUndoCheckpoint =
					options?.skipUndoCheckpoint || options?.closeOnSuccess;
				const checkpoint = zoneLastSavedDraftRef.current;
				if (!skipUndoCheckpoint && checkpoint) {
					zoneDraftHistoryRef.current.push({
						draft: { ...checkpoint },
						sensorCoordinates: {
							...zoneLastSavedSensorCoordinatesRef.current,
						},
					});
					setCanUndoZoneEdit(true);
				}

				zoneHasSavedThisSessionRef.current = true;
				setZoneHasSavedThisSession(true);
				void persistMovedZoneSensors(zoneSensorCoordinatesRef.current).then(
					() => {
						if (options?.closeOnSuccess) {
							finishZoneEdit(zone.id);
						}
					},
				);

				return true;
			}

			if (zonePersistInFlightFingerprintRef.current === fingerprint) {
				return false;
			}

			zonePersistInFlightFingerprintRef.current = fingerprint;
			const checkpoint = zoneLastSavedDraftRef.current;
			const skipUndoCheckpoint =
				options?.skipUndoCheckpoint || options?.closeOnSuccess;
			const zoneData = buildZoneUpdateFromEditDraft(draft);

			updateZone(
				{
					silent: options?.silent ?? true,
					zoneData,
					zoneId: zone.id,
				},
				{
					onError: () => {
						zonePersistInFlightFingerprintRef.current = null;

						const stillEditingSameZone = editingZoneRef.current?.id === zone.id;
						if (!stillEditingSameZone) return;

						zoneLastSavedFingerprintRef.current =
							zoneConfirmedSavedFingerprintRef.current;
						setZoneLastSavedFingerprint(
							zoneConfirmedSavedFingerprintRef.current,
						);
					},
					onSuccess: () => {
						zonePersistInFlightFingerprintRef.current = null;

						const stillEditingSameZone = editingZoneRef.current?.id === zone.id;
						if (!stillEditingSameZone) return;

						zoneHasSavedThisSessionRef.current = true;
						setZoneHasSavedThisSession(true);
						rememberZoneSavedFingerprint(fingerprint);

						if (
							!skipUndoCheckpoint &&
							checkpoint &&
							getZoneEditDraftFingerprint(checkpoint) !== fingerprint
						) {
							zoneDraftHistoryRef.current.push({
								draft: { ...checkpoint },
								sensorCoordinates: {
									...zoneLastSavedSensorCoordinatesRef.current,
								},
							});
							setCanUndoZoneEdit(true);
						}

						zoneLastSavedDraftRef.current = { ...draft };
						void persistMovedZoneSensors(zoneSensorCoordinatesRef.current).then(
							() => {
								if (options?.closeOnSuccess) {
									finishZoneEdit(zone.id);
								}
							},
						);

						if (options?.closeOnSuccess) {
							return;
						}

						const nextDraft = zoneEditDraftRef.current;
						if (
							nextDraft &&
							canPersistZoneEditDraft(nextDraft) &&
							getZoneEditDraftFingerprint(nextDraft) !==
								zoneLastSavedFingerprintRef.current
						) {
							persistZoneDraftRef.current({ silent: true });
						}
					},
				},
			);

			return true;
		},
		[
			canCustomizeLocationMap,
			finishZoneEdit,
			persistMovedZoneSensors,
			rememberZoneSavedFingerprint,
			updateZone,
		],
	);

	useEffect(() => {
		zoneEditDraftRef.current = zoneEditDraft;
		editingZoneRef.current = editingZone;
		persistZoneDraftRef.current = persistZoneDraft;
		zoneSensorCoordinatesRef.current = zoneSensorCoordinates;
		moveSensorsWithOutlineRef.current = moveSensorsWithOutline;
		autoZoneDrawPolygonRef.current = autoZoneDrawPolygon;
	});

	const {
		cancel: cancelZoneAutoSave,
		flush: flushZoneAutoSave,
		schedule: scheduleZoneAutoSave,
	} = useDebouncedCallback(() => {
		persistZoneDraft({ silent: true });
	}, SENSOR_AUTO_SAVE_DELAY_MS);

	const cancelZoneEdit = useCallback(() => {
		cancelZoneAutoSave();
		discardZoneEdit();
	}, [cancelZoneAutoSave, discardZoneEdit]);

	const cancelSensorEdit = useCallback(() => {
		flushSensorAutoSave();
		discardSensorEdit();
	}, [discardSensorEdit, flushSensorAutoSave]);

	const cancelAllEdits = useCallback(() => {
		cancelSensorEdit();
		cancelZoneEdit();
	}, [cancelSensorEdit, cancelZoneEdit]);

	const clearLocationSelection = useCallback(() => {
		cancelAllEdits();
		setSelectedLocationId(null);
		setOpenZoneIds(new Set());
		setHighlightedFloorId(null);
		setActiveFeature(undefined);
		setHoveredSensorId(undefined);
		setHoveredLocationId(undefined);
	}, [cancelAllEdits]);

	const selectLocation = useCallback(
		(locationId: string) => {
			if (!locations.some((entry) => entry.id === locationId)) return;
			if (selectedLocationIdRef.current === locationId) return;

			cancelAllEdits();
			setSelectedLocationId(locationId);
			setOpenZoneIds(new Set());
			setHighlightedFloorId(null);
			setActiveFeature(undefined);
			setHoveredSensorId(undefined);
			setHoveredLocationId(undefined);
			onLocationSelectedRef.current?.(locationId);
		},
		[cancelAllEdits, locations],
	);

	const publishMapFocus = useCallback(
		(focus: {
			floorId?: null | string;
			sensorId?: null | string;
			zoneId?: null | string;
		}) => {
			const currentLocation = locationsRef.current.find(
				(entry) => entry.id === selectedLocationIdRef.current,
			);
			const resolved = currentLocation
				? resolveLocationMapFocus(
						focus,
						toLocationMapFocusEntities([currentLocation]),
					)
				: {
						floorId: focus.floorId ?? null,
						sensorId: focus.sensorId ?? null,
						zoneId: focus.zoneId ?? null,
					};
			onMapFocusChangeRef.current?.(resolved);
		},
		[],
	);

	const zoneIdsFromPanelIndices = useCallback(
		(indices: Set<number>) =>
			new Set(
				[...indices]
					.map((index) => zones[index]?.id)
					.filter((id): id is string => Boolean(id)),
			),
		[zones],
	);

	const applyOpenZoneIdsChange = useCallback(
		(next: Set<string>) => {
			setHighlightedFloorId(null);
			const opened = [...next].filter((zoneId) => !openZoneIds.has(zoneId));

			if (opened.length === 1) {
				setOpenZoneIds(new Set([opened[0]!]));
				setActiveFeature(undefined);
				publishMapFocus({
					sensorId: null,
					zoneId: opened[0]!,
				});
				return;
			}

			setOpenZoneIds(next);
			if (next.size === 0) {
				setActiveFeature(undefined);
				publishMapFocus({ sensorId: null, zoneId: null });
			}
		},
		[openZoneIds, publishMapFocus],
	);

	const collapseAllZonePanels = useCallback(() => {
		setOpenZoneIds(new Set());
		setHighlightedFloorId(null);
		setActiveFeature(undefined);
		publishMapFocus({ sensorId: null, zoneId: null });
	}, [publishMapFocus]);

	const reorderFloors = useCallback(
		async (orderedFloorIds: string[]) => {
			const floors = location.floors ?? [];
			const updates = getFloorSortOrderUpdates(floors, orderedFloorIds);
			if (updates.length === 0) return;

			const previousLocations = locationsRef.current;
			setLocation((current) => {
				const byId = new Map(updates.map((u) => [u.floor.id, u.sortOrder]));
				return {
					...current,
					floors: (current.floors ?? []).map((floor) => {
						const sortOrder = byId.get(floor.id);
						return sortOrder === undefined ? floor : { ...floor, sortOrder };
					}),
				};
			});

			try {
				await Promise.all(
					updates.map(({ floor, sortOrder }) =>
						floorsApi.updateFloor(floor.id, toShadowFloorUpdate({ sortOrder })),
					),
				);
				onLocationsMutatedRef.current?.();
			} catch (error) {
				setLocations(previousLocations.map(cloneLocation));
				throw error;
			}
		},
		[location.floors, setLocation],
	);

	const handleOpenPanelsChange = useCallback(
		(nextIndices: Set<number>) => {
			const nextZoneIds = zoneIdsFromPanelIndices(nextIndices);

			if (editingSensorId) return;

			if (editingZoneId) {
				const isCollapsingEditedZone =
					openZoneIds.has(editingZoneId) && !nextZoneIds.has(editingZoneId);

				if (!isCollapsingEditedZone) return;

				cancelZoneEdit();
				applyOpenZoneIdsChange(nextZoneIds);
				return;
			}

			applyOpenZoneIdsChange(nextZoneIds);
		},
		[
			applyOpenZoneIdsChange,
			cancelZoneEdit,
			editingSensorId,
			editingZoneId,
			openZoneIds,
			zoneIdsFromPanelIndices,
		],
	);

	const toggleZonePanel = useCallback(
		(zoneIndex: number) => {
			const zoneId = zones[zoneIndex]?.id;
			if (!zoneId) return;

			const next = new Set(openZoneIds);
			if (next.has(zoneId)) {
				next.delete(zoneId);
			} else {
				next.add(zoneId);
			}
			handleOpenPanelsChange(
				new Set(
					[...next]
						.map((id) => zonePanelIndexById.get(id))
						.filter((index): index is number => index !== undefined),
				),
			);
		},
		[handleOpenPanelsChange, openZoneIds, zonePanelIndexById, zones],
	);

	const selectFloor = useCallback(
		(floorId: null | string) => {
			setHighlightedFloorId(floorId);
			setOpenZoneIds(new Set());
			setActiveFeature(undefined);
			setHoveredSensorId(undefined);
			publishMapFocus({ floorId, sensorId: null, zoneId: null });
		},
		[publishMapFocus],
	);

	const focusZonePanel = useCallback((zoneId: string) => {
		setHighlightedFloorId(null);
		setOpenZoneIds((current) => {
			if (current.size === 1 && current.has(zoneId)) return current;
			return new Set([zoneId]);
		});
	}, []);

	const handleMapBreadcrumbClick = useCallback(
		(crumb: MapDrillBreadcrumb) => {
			if (crumb.id === "all-locations" || crumb.level === "locations") {
				clearLocationSelection();
				publishMapFocus({ sensorId: null, zoneId: null });
				return;
			}

			cancelAllEdits();
			setHoveredSensorId(undefined);

			if (crumb.id.startsWith("sensor:")) {
				const sensorId = crumb.id.slice("sensor:".length);
				const marker = mapMarkers.find((feature) => feature.id === sensorId);
				if (marker) setActiveFeature(marker);
				if (crumb.zoneId) focusZonePanel(crumb.zoneId);
				publishMapFocus({
					sensorId,
					zoneId: crumb.zoneId ?? null,
				});
				return;
			}

			setActiveFeature(undefined);

			if (crumb.zoneId) {
				focusZonePanel(crumb.zoneId);
				publishMapFocus({
					sensorId: null,
					zoneId: crumb.zoneId,
				});
				return;
			}

			if (crumb.floorId) {
				setOpenZoneIds(new Set());
				selectFloor(crumb.floorId);
				return;
			}

			// Location crumb: stay on this location and show its first floor.
			const firstFloorId = sortFloorsForList(location.floors ?? [])[0]?.id;
			if (firstFloorId) {
				selectFloor(firstFloorId);
				return;
			}

			setOpenZoneIds(new Set());
			setHighlightedFloorId(null);
			publishMapFocus({ sensorId: null, zoneId: null });
		},
		[
			cancelAllEdits,
			clearLocationSelection,
			focusZonePanel,
			location.floors,
			mapMarkers,
			publishMapFocus,
			selectFloor,
		],
	);

	const focusSensor = useCallback(
		(sensorId: string, zoneId: string) => {
			if (isEditing) return;

			if (activeFeature?.id === sensorId) {
				setActiveFeature(undefined);
				setHoveredSensorId(undefined);
				focusZonePanel(zoneId);
				publishMapFocus({ sensorId: null, zoneId });
				return;
			}

			const marker = mapMarkers.find((feature) => feature.id === sensorId);
			if (marker) setActiveFeature(marker);

			setHoveredSensorId(sensorId);
			focusZonePanel(zoneId);
			publishMapFocus({ sensorId, zoneId });
		},
		[activeFeature?.id, focusZonePanel, isEditing, mapMarkers, publishMapFocus],
	);

	/**
	 * Always open the given zone/sensor on the map (no toggle-off).
	 * Used when hydrating focus from URL query params.
	 *
	 * Sensor pins are only in `mapMarkers` after a zone accordion is open
	 * (otherwise the map shows zone-cluster cards). Open the zone first, then
	 * select the sensor — or queue via {@link pendingActiveSensorIdRef} until
	 * markers rebuild.
	 */
	const revealOnMap = useCallback(
		(focus: {
			floorId?: null | string;
			sensorId?: null | string;
			zoneId?: null | string;
		}) => {
			if (isEditing) return false;

			const sensorId = focus.sensorId ?? undefined;
			const zoneId = focus.zoneId ?? undefined;
			const floorId = focus.floorId ?? undefined;

			if (sensorId && !zoneId) {
				return false;
			}

			if (sensorId && zoneId) {
				// Must expand the zone before sensor markers exist on the map.
				focusZonePanel(zoneId);

				const marker = mapMarkers.find(
					(feature) => String(feature.id ?? "") === sensorId,
				);
				if (marker) {
					pendingActiveSensorIdRef.current = null;
					setActiveFeature(marker);
					setHoveredSensorId(sensorId);
					return true;
				}

				// Zone open (or opening) — wait for sensor-marker rebuild.
				pendingActiveSensorIdRef.current = sensorId;
				setHoveredSensorId(sensorId);
				return true;
			}

			if (zoneId) {
				pendingActiveSensorIdRef.current = null;
				setActiveFeature(undefined);
				setHoveredSensorId(undefined);
				focusZonePanel(zoneId);
				return true;
			}

			if (floorId) {
				pendingActiveSensorIdRef.current = null;
				selectFloor(floorId);
				return true;
			}

			pendingActiveSensorIdRef.current = null;
			setActiveFeature(undefined);
			setHoveredSensorId(undefined);
			setOpenZoneIds(new Set());
			setHighlightedFloorId(null);
			return true;
		},
		[focusZonePanel, isEditing, mapMarkers, selectFloor],
	);

	const hoverSensor = useCallback(
		(sensorId: string) => {
			if (isEditing) return;
			setHoveredSensorId(sensorId);
		},
		[isEditing],
	);

	const clearHoveredSensor = useCallback(() => {
		setHoveredSensorId(undefined);
	}, []);

	const hoverLocation = useCallback((locationId: string) => {
		setHoveredLocationId(locationId);
	}, []);

	const clearHoveredLocation = useCallback(() => {
		setHoveredLocationId(undefined);
	}, []);

	const handleMarkerHover = useCallback(
		(feature: GeoJSON.Feature) => {
			if (!isSensorMarkerFeature(feature) || isEditing) return;

			const sensorId = String(feature.id ?? "");
			if (sensorId) setHoveredSensorId(sensorId);
		},
		[isEditing],
	);

	const handleMarkerHoverEnd = useCallback(() => {
		clearHoveredSensor();
	}, [clearHoveredSensor]);

	const startZoneEdit = useCallback(
		(zone: ShadowZone) => {
			if (!canCustomizeLocationMap || isFloorEditActive) return;

			cancelSensorEdit();
			const draft = createZoneEditDraft(zone, {
				perimeterLine: lineBoundariesByZoneId[zone.id],
			});
			const fingerprint = getZoneEditDraftFingerprint(draft);
			rememberZoneSavedFingerprint(fingerprint);
			const seededLine = storedBoundaryToLineString(draft.boundary);
			if (seededLine) {
				setLineBoundariesByZoneId((current) => ({
					...current,
					[zone.id]:
						pickRicherLineString(current[zone.id], seededLine) ?? seededLine,
				}));
			}
			zoneHasSavedThisSessionRef.current = false;
			setZoneHasSavedThisSession(false);
			zonePersistInFlightFingerprintRef.current = null;
			clearZoneDraftHistory();
			zoneInitialDraftRef.current = { ...draft };
			zoneLastSavedDraftRef.current = { ...draft };

			const sensorCoordinates = snapshotMappedSensorCoordinates(
				zone.sensors ?? [],
			);
			setMoveSensorsWithOutline(true);
			setZoneSensorCoordinates(sensorCoordinates);
			zoneSensorCoordinatesRef.current = sensorCoordinates;
			zoneInitialSensorCoordinatesRef.current = sensorCoordinates;
			zoneLastSavedSensorCoordinatesRef.current = {
				...sensorCoordinates,
			};

			setEditingZoneId(zone.id);
			setZoneEditDraft(draft);
			setActiveFeature(undefined);
			setHoveredSensorId(undefined);
			focusZonePanel(zone.id);
		},
		[
			canCustomizeLocationMap,
			cancelSensorEdit,
			clearZoneDraftHistory,
			focusZonePanel,
			isFloorEditActive,
			rememberZoneSavedFingerprint,
			lineBoundariesByZoneId,
		],
	);

	const startSensorEdit = useCallback(
		(sensor: ShadowSensor, zoneId: string) => {
			if (!canCustomizeLocationMap || isFloorEditActive) return;

			flushSensorAutoSave();
			flushZoneAutoSave();
			cancelZoneEdit();

			const draft = createSensorEditDraft(sensor, zoneId);
			const fingerprint = getSensorEditDraftFingerprint(draft);
			rememberSavedFingerprint(fingerprint);
			hasSavedThisSessionRef.current = false;
			setHasSavedThisSession(false);
			persistInFlightFingerprintRef.current = null;
			clearDraftHistory();
			initialDraftRef.current = { ...draft };
			lastSavedDraftRef.current = { ...draft };

			setEditingSensorId(sensor.id);
			setEditDraft(draft);
			setHoveredSensorId(undefined);

			const marker = mapMarkers.find((feature) => feature.id === sensor.id);
			if (marker) {
				setActiveFeature(marker);
			}

			focusZonePanel(zoneId);
		},
		[
			canCustomizeLocationMap,
			cancelZoneEdit,
			clearDraftHistory,
			flushSensorAutoSave,
			flushZoneAutoSave,
			focusZonePanel,
			isFloorEditActive,
			mapMarkers,
			rememberSavedFingerprint,
		],
	);

	const isZoneEditDirty = useMemo(() => {
		if (!editingZone || !zoneEditDraft) return false;

		return (
			zoneEditDraft.name.trim() !== toTitleCase(editingZone.name).trim() ||
			normalizeZoneColor(zoneEditDraft.color) !==
				normalizeZoneColor(editingZone.color) ||
			zoneEditDraft.boundaryMode !== resolveZoneBoundaryMode(editingZone) ||
			zoneEditDraft.boundaryKind !== resolveZoneBoundaryKind(editingZone) ||
			normalizeZoneBorderWidth(zoneEditDraft.borderWidth) !==
				normalizeZoneBorderWidth(editingZone.borderWidth) ||
			zoneEditDraft.boundary?.type !== editingZone.boundary?.type ||
			JSON.stringify(zoneEditDraft.boundary?.coordinates ?? null) !==
				JSON.stringify(editingZone.boundary?.coordinates ?? null) ||
			!areSensorLngLatByIdEqual(
				zoneSensorCoordinates,
				snapshotMappedSensorCoordinates(editingZone.sensors ?? []),
			)
		);
	}, [editingZone, zoneEditDraft, zoneSensorCoordinates]);

	const handleSaveZoneEdit = useCallback(() => {
		cancelZoneAutoSave();

		const draft = zoneEditDraftRef.current;
		if (!draft || !canPersistZoneEditDraft(draft)) return;

		flushZoneAutoSave();
		persistZoneDraft({ closeOnSuccess: true, silent: false });
	}, [cancelZoneAutoSave, flushZoneAutoSave, persistZoneDraft]);

	const handleUndoZoneEdit = useCallback(() => {
		cancelZoneAutoSave();

		const previous = zoneDraftHistoryRef.current.pop();
		setCanUndoZoneEdit(zoneDraftHistoryRef.current.length > 0);
		if (!previous) return;

		zoneEditDraftRef.current = previous.draft;
		setZoneEditDraft(previous.draft);
		setZoneSensorCoordinates(previous.sensorCoordinates);
		zoneSensorCoordinatesRef.current = previous.sensorCoordinates;
		persistZoneDraft({ silent: true, skipUndoCheckpoint: true });
	}, [cancelZoneAutoSave, persistZoneDraft]);

	const handleCancelZoneEdit = useCallback(() => {
		cancelZoneAutoSave();

		const initial = zoneInitialDraftRef.current;
		const zone = editingZoneRef.current;
		if (!initial || !zone) {
			discardZoneEdit();
			return;
		}

		zoneEditDraftRef.current = initial;
		setZoneEditDraft(initial);
		setZoneSensorCoordinates(zoneInitialSensorCoordinatesRef.current);
		zoneSensorCoordinatesRef.current = zoneInitialSensorCoordinatesRef.current;
		clearZoneDraftHistory();

		if (!canPersistZoneEditDraft(initial)) {
			discardZoneEdit();
			return;
		}

		persistZoneDraft({ closeOnSuccess: true, silent: true });
	}, [
		cancelZoneAutoSave,
		clearZoneDraftHistory,
		discardZoneEdit,
		persistZoneDraft,
	]);

	const updateZoneEditDraft = useCallback(
		(updates: Partial<ShadowZoneEditDraft>) => {
			setZoneEditDraft((current) => {
				if (!current) return current;

				let next: ShadowZoneEditDraft = { ...current, ...updates };

				if (
					updates.boundaryMode === "custom" &&
					current.boundaryMode !== "custom" &&
					next.boundaryKind !== "line"
				) {
					// Explicit clear (Remove outline from Auto) must stay empty so the
					// operator can re-enable Auto or draw Custom from a blank map.
					if (updates.boundary === null) {
						next = { ...next, boundary: null };
					} else if (updates.boundary !== undefined) {
						// Keep the live Draw geometry (e.g. Auto → Custom via vertex drag).
						next = { ...next, boundary: updates.boundary };
					} else {
						const zone = editingZoneRef.current;
						const seeded = zone
							? seedManualZoneBoundary({
									...zone,
									boundaryKind: next.boundaryKind,
									sensors: applySensorCoordinatesToSensors(
										zone.sensors ?? [],
										zoneSensorCoordinatesRef.current,
									),
								})
							: undefined;
						if (seeded) {
							next = { ...next, boundary: seeded };
						}
					}
				}

				if (
					updates.boundaryKind &&
					updates.boundaryKind !== current.boundaryKind
				) {
					const zone = editingZoneRef.current;
					next = {
						...next,
						boundary:
							updates.boundaryKind === "line" && zone
								? seedManualZoneBoundary({
										...zone,
										boundary: undefined,
										boundaryKind: "line",
										sensors: applySensorCoordinatesToSensors(
											zone.sensors ?? [],
											zoneSensorCoordinatesRef.current,
										),
									})
								: undefined,
						boundaryMode: updates.boundaryKind === "line" ? "custom" : "auto",
					};
				}

				if (updates.boundary === null && next.boundaryKind === "line") {
					const zoneId = editingZoneRef.current?.id;
					if (zoneId) {
						setLineBoundariesByZoneId((current) => {
							if (!(zoneId in current)) return current;
							const nextOverrides = { ...current };
							delete nextOverrides[zoneId];
							return nextOverrides;
						});
					}
				}

				if (updates.boundaryMode === "auto" && next.boundaryKind !== "line") {
					next = { ...next, boundary: undefined };
				}

				zoneEditDraftRef.current = next;
				return next;
			});
		},
		[],
	);

	const handleZoneBoundaryDrawChange = useCallback(
		(boundary: null | ZoneBoundary, action?: ZoneBoundaryDrawAction) => {
			const draft = zoneEditDraftRef.current;
			if (draft?.boundaryKind === "line") {
				const line =
					boundary?.type === "LineString"
						? (storedBoundaryToLineString(boundary) ?? boundary)
						: boundary?.type === "Polygon"
							? polygonToLineStringBoundary(boundary)
							: undefined;
				if (!line) {
					if (action === "delete") {
						const zoneId = editingZoneRef.current?.id;
						if (zoneId) {
							setLineBoundariesByZoneId((current) => {
								if (!(zoneId in current)) return current;
								const nextOverrides = { ...current };
								delete nextOverrides[zoneId];
								return nextOverrides;
							});
						}
						updateZoneEditDraft({ boundary: null });
					}
					return;
				}

				const zoneId = editingZoneRef.current?.id;
				if (zoneId) {
					setLineBoundariesByZoneId((current) => ({
						...current,
						[zoneId]: line,
					}));
				}

				updateZoneEditDraft({ boundary: line, boundaryMode: "custom" });

				if (action === "move") {
					setZoneSensorCoordinates((current) => {
						const next = snapSensorLngLatByIdToLine(current, line);
						zoneSensorCoordinatesRef.current = next;
						return next;
					});
				}
				return;
			}

			const isAutoOutline = draft?.boundaryMode === "auto";

			if (isAutoOutline) {
				// Vertex reshape: freeze the current Auto hull as Custom and keep
				// the live geometry so the drag continues without remounting Draw.
				if (action === "change_coordinates" && boundary) {
					updateZoneEditDraft({
						boundary,
						boundaryMode: "custom",
					});
					return;
				}

				if (action !== "move" || !boundary) return;

				const delta = getPolygonTranslationDelta(
					autoZoneDrawPolygonRef.current,
					boundary,
				);
				if (!delta) return;

				setZoneSensorCoordinates((current) => {
					const next = translateSensorLngLatById(current, delta);
					zoneSensorCoordinatesRef.current = next;
					return next;
				});
				return;
			}

			const previousBoundary = zoneEditDraftRef.current?.boundary;
			updateZoneEditDraft({ boundary });

			if (
				action !== "move" ||
				!moveSensorsWithOutlineRef.current ||
				!previousBoundary ||
				!boundary
			) {
				return;
			}

			const delta = getPolygonTranslationDelta(previousBoundary, boundary);
			if (!delta) return;

			setZoneSensorCoordinates((current) => {
				const next = translateSensorLngLatById(current, delta);
				zoneSensorCoordinatesRef.current = next;
				return next;
			});
		},
		[updateZoneEditDraft],
	);

	const sensorsOutsideManualBoundary = useMemo(() => {
		if (!zoneEditDraft || zoneEditDraft.boundaryMode !== "custom") return [];
		if (zoneEditDraft.boundaryKind === "line") return [];
		if (isZoneEditDraftBoundarySelfIntersecting(zoneEditDraft)) return [];

		return getSensorsOutsideZoneBoundary(
			applySensorCoordinatesToSensors(
				editingZone?.sensors ?? [],
				zoneSensorCoordinates,
			),
			zoneEditDraft.boundary,
		);
	}, [editingZone?.sensors, zoneEditDraft, zoneSensorCoordinates]);

	const previewZoneSensorOnMap = useCallback(
		(sensorId: string) => {
			const marker = displayMapMarkers.find(
				(feature) => feature.id === sensorId,
			);
			if (marker) setActiveFeature(marker);
			setHoveredSensorId(sensorId);
		},
		[displayMapMarkers],
	);

	const hoverZoneSensorOnMap = useCallback((sensorId?: string) => {
		setHoveredSensorId(sensorId);
	}, []);

	const handleExpandZoneBoundaryToIncludeOutsideSensors = useCallback(() => {
		if (!zoneEditDraft?.boundary || zoneEditDraft.boundary.type !== "Polygon") {
			return;
		}

		const expanded = expandGeoJsonPolygonToIncludePoints(
			zoneEditDraft.boundary,
			sensorsOutsideManualBoundary.map((sensor) => sensor.coordinates),
		);
		if (!expanded) return;

		setActiveFeature(undefined);
		updateZoneEditDraft({ boundary: expanded });
	}, [sensorsOutsideManualBoundary, updateZoneEditDraft, zoneEditDraft]);

	const zoneBoundaryDraw = useMemo((): undefined | ZoneBoundaryDrawConfig => {
		// buildZoneBoundaryDraw only stores onChange / registerZoneBoundaryCapture;
		// neither is invoked during render. eslint react-hooks/refs cannot see that.
		/* eslint-disable react-hooks/refs -- draw config holds ref-backed callbacks for Mapbox Draw */
		return buildZoneBoundaryDraw({
			autoZoneDrawPolygon,
			editingZoneId,
			onChange: handleZoneBoundaryDrawChange,
			registerZoneBoundaryCapture,
			zoneEditDraft,
		});
		/* eslint-enable react-hooks/refs */
	}, [
		autoZoneDrawPolygon,
		editingZoneId,
		handleZoneBoundaryDrawChange,
		registerZoneBoundaryCapture,
		zoneEditDraft,
	]);

	const applyDraftUpdate = useCallback(
		(updater: (current: ShadowSensorEditDraft) => ShadowSensorEditDraft) => {
			setEditDraft((current) => {
				if (!current) return current;

				const next = updater(current);
				if (areSensorEditDraftsEqual(current, next)) {
					return current;
				}

				return next;
			});
		},
		[],
	);

	const handleEditZoneChange = useCallback(
		(zoneId: string) => {
			applyDraftUpdate((current) => ({ ...current, zoneId }));
			focusZonePanel(zoneId);
		},
		[applyDraftUpdate, focusZonePanel],
	);

	const handleMapCoordinateChange = useCallback(
		(coordinates: [number, number]) => {
			if (!canCustomizeLocationMap || !editingSensorId) return;
			applyDraftUpdate((current) => ({
				...current,
				latitude: String(coordinates[1]),
				longitude: String(coordinates[0]),
			}));
		},
		[applyDraftUpdate, canCustomizeLocationMap, editingSensorId],
	);

	const handleMarkerDragEnd = useCallback(
		(_feature: GeoJSON.Feature, coordinates: [number, number]) => {
			handleMapCoordinateChange(coordinates);
		},
		[handleMapCoordinateChange],
	);

	const handleSaveSensorEdit = useCallback(() => {
		cancelSensorAutoSave();
		persistSensorDraft({ closeOnSuccess: true, silent: false });
	}, [cancelSensorAutoSave, persistSensorDraft]);

	const handleUndoSensorEdit = useCallback(() => {
		cancelSensorAutoSave();

		const previous = draftHistoryRef.current.pop();
		setCanUndoSensorEdit(draftHistoryRef.current.length > 0);
		if (!previous) return;

		editDraftRef.current = previous;
		setEditDraft(previous);
		persistSensorDraft({ silent: true, skipUndoCheckpoint: true });
	}, [cancelSensorAutoSave, persistSensorDraft]);

	const handleCancelSensorEdit = useCallback(() => {
		cancelSensorAutoSave();

		const initial = initialDraftRef.current;
		const context = editingSensorContextRef.current;
		if (!initial || !context) {
			discardSensorEdit();
			return;
		}

		editDraftRef.current = initial;
		setEditDraft(initial);
		clearDraftHistory();

		if (!canPersistSensorEditDraft(initial)) {
			discardSensorEdit();
			return;
		}

		persistSensorDraft({ closeOnSuccess: true, silent: true });
	}, [
		cancelSensorAutoSave,
		clearDraftHistory,
		discardSensorEdit,
		persistSensorDraft,
	]);

	useEffect(() => {
		if (!editingSensorId || !editDraft) {
			cancelSensorAutoSave();
			return;
		}

		const fingerprint = getSensorEditDraftFingerprint(editDraft);
		if (fingerprint === lastSavedDraftFingerprintRef.current) return;
		if (persistInFlightFingerprintRef.current === fingerprint) return;
		if (!canPersistSensorEditDraft(editDraft)) return;

		scheduleSensorAutoSave();
	}, [
		cancelSensorAutoSave,
		editDraft,
		editingSensorId,
		scheduleSensorAutoSave,
	]);

	useEffect(() => {
		if (!editingZoneId || !zoneEditDraft) {
			cancelZoneAutoSave();
			return;
		}

		const fingerprint = getZoneEditDraftFingerprint(zoneEditDraft);
		const sensorsChanged = !areSensorLngLatByIdEqual(
			zoneSensorCoordinates,
			zoneLastSavedSensorCoordinatesRef.current,
		);
		if (
			fingerprint === zoneLastSavedFingerprintRef.current &&
			!sensorsChanged
		) {
			return;
		}
		if (
			fingerprint !== zoneLastSavedFingerprintRef.current &&
			zonePersistInFlightFingerprintRef.current === fingerprint
		) {
			return;
		}
		if (!canPersistZoneEditDraft(zoneEditDraft)) {
			cancelZoneAutoSave();
			return;
		}

		scheduleZoneAutoSave();
	}, [
		cancelZoneAutoSave,
		editingZoneId,
		scheduleZoneAutoSave,
		zoneEditDraft,
		zoneSensorCoordinates,
	]);

	useEffect(() => {
		if (!editingSensorId) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				// Ignore only if something else already handled Escape (e.g. open Select).
				if (event.isComposing || event.defaultPrevented) return;
				event.preventDefault();
				handleCancelSensorEdit();
				return;
			}

			if (event.key === " " || event.code === "Space") return;
			if (isSensorEditHotkeyBlocked(event)) return;

			if (event.key === "Enter" && event.code === "Enter") {
				if (isSensorEditEnterTarget(event)) return;
				event.preventDefault();
				handleSaveSensorEdit();
			}
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [editingSensorId, handleCancelSensorEdit, handleSaveSensorEdit]);

	useEffect(() => {
		if (!editingZoneId) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				// Zone color swatches use role="listbox", which would otherwise block
				// Escape via isSensorEditHotkeyBlocked — handle cancel before that check.
				if (event.isComposing || event.defaultPrevented) return;
				event.preventDefault();
				handleCancelZoneEdit();
				return;
			}

			if (event.key === " " || event.code === "Space") return;
			if (isSensorEditHotkeyBlocked(event)) return;

			if (event.key === "Enter" && event.code === "Enter") {
				if (isSensorEditEnterTarget(event)) return;
				event.preventDefault();
				handleSaveZoneEdit();
			}
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [editingZoneId, handleCancelZoneEdit, handleSaveZoneEdit]);

	const handleMarkerClick = useCallback(
		(feature: GeoJSON.Feature) => {
			if (isFloorEditActive) return;
			if (isClusterMarkerFeature(feature)) {
				const kind = feature.properties?.kind;
				const locationId =
					typeof feature.properties?.locationId === "string"
						? feature.properties.locationId
						: undefined;
				const zoneId =
					typeof feature.properties?.zoneId === "string"
						? feature.properties.zoneId
						: undefined;
				if (kind === "locationCluster" && locationId) {
					if (onLocationClusterSelectRef.current) {
						onLocationClusterSelectRef.current(locationId);
					} else {
						selectLocation(locationId);
					}
					return;
				}
				if (kind === "zoneCluster" && zoneId) {
					setActiveFeature(undefined);
					setHoveredSensorId(undefined);
					focusZonePanel(zoneId);
					publishMapFocus({ sensorId: null, zoneId });
				}
				return;
			}

			if (!isSensorMarkerFeature(feature)) return;

			const sensorId = String(feature.id ?? "");

			// Switch edit target while already editing a sensor or zone.
			if (editingSensorId || editingZoneId) {
				if (editingSensorId && sensorId === editingSensorId) {
					handleSaveSensorEdit();
					return;
				}

				if (!canEditSensorDetails) {
					if (editingSensorId) handleSaveSensorEdit();
					return;
				}

				const found = findSensorInLocation(location, sensorId);
				if (!found) return;

				startSensorEdit(found.sensor, found.zoneId);
				return;
			}

			if (activeFeature?.id === feature.id) {
				setActiveFeature(undefined);
				setHoveredSensorId(undefined);
				const zoneId = getMarkerZoneId(feature);
				publishMapFocus({
					sensorId: null,
					zoneId: zoneId ?? null,
				});
				return;
			}

			const zoneId = getMarkerZoneId(feature);
			if (zoneId) {
				focusZonePanel(zoneId);
			}

			setHoveredSensorId(sensorId);
			setActiveFeature(feature);
			publishMapFocus({
				sensorId,
				zoneId: zoneId ?? null,
			});
		},
		[
			activeFeature?.id,
			canEditSensorDetails,
			editingSensorId,
			editingZoneId,
			focusZonePanel,
			handleSaveSensorEdit,
			isFloorEditActive,
			publishMapFocus,
			location,
			selectLocation,
			startSensorEdit,
		],
	);

	const handleOverlayClick = useCallback(
		(feature: GeoJSON.Feature) => {
			if (isFloorEditActive) return;
			if (feature.properties?.overlayKind === "floor") return;

			const zoneId = getOverlayZoneId(feature);
			if (!zoneId) return;

			// Switch to editing the clicked zone while already in edit mode.
			if (editingSensorId || editingZoneId) {
				if (zoneId === editingZoneId) return;
				if (!canEditZoneDetails) return;

				const zone = location.zones?.find((entry) => entry.id === zoneId);
				if (!zone) return;

				startZoneEdit(zone);
				return;
			}

			setActiveFeature(undefined);
			setHoveredSensorId(undefined);
			focusZonePanel(zoneId);
			publishMapFocus({ sensorId: null, zoneId });
		},
		[
			canEditZoneDetails,
			editingSensorId,
			editingZoneId,
			focusZonePanel,
			isFloorEditActive,
			location.zones,
			publishMapFocus,
			startZoneEdit,
		],
	);

	const handleOverlayDoubleClick = useCallback(
		(feature: GeoJSON.Feature) => {
			if (!canEditZoneDetails || isFloorEditActive) return;

			const zoneId = getOverlayZoneId(feature);
			if (!zoneId || zoneId === editingZoneId) return;

			const zone = location.zones?.find((entry) => entry.id === zoneId);
			if (!zone) return;

			startZoneEdit(zone);
		},
		[
			canEditZoneDetails,
			editingZoneId,
			isFloorEditActive,
			location.zones,
			startZoneEdit,
		],
	);

	const handlePopupClose = useCallback(() => {
		if (editingSensorId) return;

		const zoneId =
			activeFeature && getMarkerZoneId(activeFeature)
				? getMarkerZoneId(activeFeature)
				: openZoneIds.size === 1
					? [...openZoneIds][0]!
					: null;

		setActiveFeature(undefined);
		setHoveredSensorId(undefined);
		publishMapFocus({ sensorId: null, zoneId });
	}, [activeFeature, editingSensorId, openZoneIds, publishMapFocus]);

	const handleMarkerEdit = useCallback(
		(feature: GeoJSON.Feature) => {
			if (!canEditSensorDetails || isFloorEditActive) return;
			if (!isSensorMarkerFeature(feature)) return;

			const sensorId = String(feature.id ?? "");
			if (!sensorId) return;
			if (sensorId === editingSensorId) {
				handleSaveSensorEdit();
				return;
			}

			const found = findSensorInLocation(location, sensorId);
			if (!found) return;

			startSensorEdit(found.sensor, found.zoneId);
		},
		[
			canEditSensorDetails,
			editingSensorId,
			handleSaveSensorEdit,
			isFloorEditActive,
			location,
			startSensorEdit,
		],
	);

	const updateEditDraft = applyDraftUpdate;

	const totalSensors = useMemo(
		() => zones.reduce((count, zone) => count + (zone.sensors?.length ?? 0), 0),
		[zones],
	);

	const zoneMoveHint = useMemo(() => {
		if (!editingSensorContext || !editDraft) return undefined;

		const { zoneId: savedZoneId } = editingSensorContext;
		if (editDraft.zoneId === savedZoneId) return undefined;

		const savedZone = zones.find((zone) => zone.id === savedZoneId);
		const targetZone = zones.find((zone) => zone.id === editDraft.zoneId);
		if (!targetZone) return undefined;

		const fromLabel = savedZone ? toTitleCase(savedZone.name) : "current zone";
		const toLabel = toTitleCase(targetZone.name);

		return `Moving from ${fromLabel} to ${toLabel}.`;
	}, [editDraft, editingSensorContext, zones]);

	const autoSaveStatus = useMemo((): SensorAutoSaveStatus => {
		return resolveEditAutoSaveStatus({
			canPersist: editDraft ? canPersistSensorEditDraft(editDraft) : false,
			fingerprint: editDraft ? getSensorEditDraftFingerprint(editDraft) : "",
			hasDraft: Boolean(editDraft && editingSensorId),
			hasSavedThisSession,
			isPending: isUpdatedSensorPending,
			lastSavedFingerprint,
		});
	}, [
		editDraft,
		editingSensorId,
		hasSavedThisSession,
		isUpdatedSensorPending,
		lastSavedFingerprint,
	]);

	const zoneAutoSaveStatus = useMemo((): SensorAutoSaveStatus => {
		return resolveEditAutoSaveStatus({
			canPersist: zoneEditDraft
				? canPersistZoneEditDraft(zoneEditDraft)
				: false,
			fingerprint: zoneEditDraft
				? getZoneEditDraftFingerprint(zoneEditDraft)
				: "",
			hasDraft: Boolean(zoneEditDraft && editingZoneId),
			hasSavedThisSession: zoneHasSavedThisSession,
			isPending: isUpdatedZonePending,
			lastSavedFingerprint: zoneLastSavedFingerprint,
		});
	}, [
		editingZoneId,
		isUpdatedZonePending,
		zoneEditDraft,
		zoneHasSavedThisSession,
		zoneLastSavedFingerprint,
	]);

	const resetToSource = useCallback(() => {
		if (sourceLocations == null) return;
		lastSyncedRevisionRef.current = null;
		setLocations(sourceLocations.map(cloneLocation));
		setSelectedLocationId(null);
		setOpenZoneIds(new Set());
		setHighlightedFloorId(null);
		setEditingSensorId(null);
		setEditDraft(null);
		setEditingZoneId(null);
		setZoneEditDraft(null);
		setActiveFeature(undefined);
		setHoveredSensorId(undefined);
		setHoveredLocationId(undefined);
		setZoneSensorCoordinates({});
		setMoveSensorsWithOutline(true);
		setLineBoundariesByZoneId({});
	}, [sourceLocations]);

	return {
		activeFeature,
		autoSaveStatus,
		cancelAllEdits,
		cancelSensorEdit,
		cancelZoneEdit,
		canEditSensorDetails,
		canEditZoneDetails,
		canUndoSensorEdit,
		canUndoZoneEdit,
		clearHoveredLocation,
		clearHoveredSensor,
		clearLocationSelection,
		collapseAllZonePanels,
		displayMapMarkers,
		editDraft,
		editingSensorContext,
		editingSensorId,
		editingZone,
		editingZoneId,
		editTypeOptions,
		editZoneOptions,
		emphasizedLocationClusterIds,
		emphasizedSensorIds,
		fitMapMarkers,
		focusSensor,
		handleCancelSensorEdit,
		handleCancelZoneEdit,
		handleEditZoneChange,
		handleExpandZoneBoundaryToIncludeOutsideSensors,
		handleMapBreadcrumbClick,
		handleMapCoordinateChange,
		handleMarkerClick,
		handleMarkerDragEnd,
		handleMarkerEdit,
		handleMarkerHover,
		handleMarkerHoverEnd,
		handleOpenPanelsChange,
		handleOverlayClick,
		handleOverlayDoubleClick,
		handlePopupClose,
		handleSaveSensorEdit,
		handleSaveZoneEdit,
		handleUndoSensorEdit,
		handleUndoZoneEdit,
		highlightedFloorId,
		hoveredLocationId,
		hoveredSensorId,
		hoverLocation,
		hoverSensor,
		hoverZoneSensorOnMap,
		isEditDirty,
		isEditing,
		isFloorEditActive,
		isLocationSelected,
		isUpdatedSensorPending,
		isUpdatedZonePending,
		isZoneEditDirty,
		location,
		locations,
		mapBreadcrumbs,
		mapFocusFitKey,
		mapMarkers,
		mappableLocationCount,
		moveSensorsWithOutline,
		openPanels,
		previewZoneSensorOnMap,
		reorderFloors,
		resetToSource,
		revealOnMap,
		selectedLocationId,
		selectFloor,
		selectLocation,
		sensorsOutsideManualBoundary,
		setFloorEditActive,
		setMoveSensorsWithOutline,
		showEmptyFocusedZoneMessage,
		startSensorEdit,
		startZoneEdit,
		toggleZonePanel,
		totalSensors,
		unmappedLocationCount,
		unmappedLocations,
		updateEditDraft,
		updateZoneEditDraft,
		visibleFloorId,
		visibleMapCenter,
		visibleMapMarkers,
		visibleMapOverlays,
		zoneAutoSaveStatus,
		zoneBoundaryDraw,
		zoneEditDraft,
		zoneMoveHint,
		zones,
	};
};

export type LocationManagementState = ReturnType<
	typeof useLocationManagementState
>;
